"use client";

/**
 * action-timeline.tsx — 行动节拍条:这手牌怎么走到你面前
 *
 * F1 折叠(§0 视觉降密):默认收起为一行——「行动线」标签 + 一句白话压缩 + 金色「轮到你」
 * 徽标 + 展开 affordance;点击展开完整 chip 序列。白话压缩从已发生 steps(≤决策时刻)
 * 派生(jargonZh/sanitize 现有管线,箭头连接 = action_line 自身约定),0 新增解释文字。
 *
 * 把 action_line("UTG平跟 → 后方大尺度挤压")解析成水平节拍:
 * 每步一个 chip(角色徽标 + jargonZh 中文化动作),最后一个「你」的节点 = 决策时刻(金色高亮)。
 * hero 识别:显式 Hero/英雄/你,或步骤角色与 heroPosition 解析到同一座位(如 hero=UTG 时的「UTG平跟」);
 * 都解析不出时兜底把末步视作决策时刻(行动线的尽头就是你)。
 *
 * Q(防剧透):决策时刻之后的节拍 = 「假如你这么选」的未来分支,
 * 虚线 + 灰化 + 首个 chip 挂「假设」徽标,与已发生行动(实线)视觉区分。
 * 白话压缩只取已发生部分,未来分支不剧透进收起态。
 */

import { useId, useMemo, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import { ChevronDown } from "lucide-react";
import { jargonZh } from "@/lib/jargon-zh";
import { positionZh, DECISION_SCREEN_GLOSS } from "@/lib/poker-terms-zh";
import { sanitizeSuitedZh } from "@/lib/poker-cards";
import { renderGlossEntries } from "@/components/quiz-gloss";
import { normalizePosition } from "@/components/poker-table";
import { EASE_OUT } from "@/components/dojo-theme";
import { cn } from "@/lib/cn";

type ActionStep = { actor: string; action: string; isHero: boolean };

const ACTOR_PREFIX = /^(UTG\+1|UTG|LJ|HJ|MP|CO|BTN|SB|BB|EP|LP|Hero|Villain|你|对手)\s*[:：]?\s*/i;

function isHeroActor(actor: string, heroSeat: ReturnType<typeof normalizePosition>): boolean {
  if (/hero|英雄|你/i.test(actor)) return true;
  return heroSeat !== null && normalizePosition(actor) === heroSeat;
}

function parseActionSteps(
  line: string,
  heroSeat: ReturnType<typeof normalizePosition>,
): ActionStep[] {
  if (!line) return [];
  return line
    .split(/[→;；]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((step) => {
      const colon = /^(.+?)[：:]\s*(.+)$/.exec(step);
      if (colon) {
        const actor = colon[1].trim();
        return { actor, action: colon[2].trim(), isHero: isHeroActor(actor, heroSeat) };
      }
      const prefix = ACTOR_PREFIX.exec(step);
      if (prefix) {
        const actor = prefix[1];
        return {
          actor,
          action: step.slice(prefix[0].length).trim() || step,
          isHero: isHeroActor(actor, heroSeat),
        };
      }
      if (/hero|英雄/i.test(step)) {
        return {
          actor: "Hero",
          action: step.replace(/hero|英雄/i, "").trim() || step,
          isHero: true,
        };
      }
      return { actor: "", action: step, isHero: false };
    });
}

export function ActionTimeline({
  actionLine,
  heroPosition,
}: {
  actionLine?: string | null;
  heroPosition?: string | null;
}) {
  const reduce = useReducedMotion();
  const regionId = useId();
  // F1:默认收起(§0 降密);多实例各自 useState 隔离;不持久化(每手新牌都从低密态开始)
  const [open, setOpen] = useState(false);
  // 换一手牌(actionLine 变)回到收起态——渲染期派生态,与 spot-prompt 的 prevSceneId 同款模式
  const [prevLine, setPrevLine] = useState(actionLine);
  if (prevLine !== actionLine) {
    setPrevLine(actionLine);
    setOpen(false);
  }
  const heroSeat = useMemo(
    () => normalizePosition(heroPosition ?? null),
    [heroPosition],
  );
  const steps = useMemo(
    () => parseActionSteps(actionLine ?? "", heroSeat),
    [actionLine, heroSeat],
  );
  if (steps.length === 0) return null;

  let decisionIdx = -1;
  for (let i = steps.length - 1; i >= 0; i--) {
    if (steps[i].isHero) {
      decisionIdx = i;
      break;
    }
  }
  if (decisionIdx === -1) decisionIdx = steps.length - 1;

  // 收起态一句白话:已发生各步(≤决策时刻)动作经现有中文化管线后箭头连接,
  // 纯派生压缩——不新写解释散文;未来(假设)分支不进收起态。单行 truncate 兜底长度。
  const summary = steps
    .slice(0, decisionIdx + 1)
    .map((s) => sanitizeSuitedZh(jargonZh(s.action)).trim())
    .filter(Boolean)
    .join(" → ");

  const list: Variants = {
    hidden: {},
    visible: { transition: { staggerChildren: reduce ? 0 : 0.06 } },
  };
  const item: Variants = {
    hidden: { opacity: 0, x: reduce ? 0 : -8 },
    visible: {
      opacity: 1,
      x: 0,
      transition: reduce ? { duration: 0.2 } : { duration: 0.32, ease: EASE_OUT },
    },
  };
  const region: Variants = {
    collapsed: { height: 0, opacity: 0 },
    open: { height: "auto", opacity: 1 },
  };

  return (
    <div className="rounded-xl border border-[#1F2E24] bg-[#0C130E] px-3.5 py-3">
      {/* 收起态(默认):整行可点 = 展开/收起;金色「轮到你」徽标保住决策时刻存在感 */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={regionId}
        className="group -mx-1.5 flex w-[calc(100%+0.75rem)] items-center gap-2.5 rounded-md px-1.5 py-1 text-left transition-colors hover:bg-white/[0.03] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/70"
      >
        <span className="shrink-0 text-[10px] font-semibold tracking-[0.22em] text-stone-500">
          行动线
        </span>
        {summary && (
          <span className="min-w-0 flex-1 truncate text-xs text-stone-400">
            {summary}
          </span>
        )}
        {!summary && <span className="flex-1" />}
        <span className="flex shrink-0 items-center gap-1 rounded-full border border-amber-300/40 bg-[#261E0D] px-2 py-0.5 text-[10px] font-bold text-amber-200">
          {!reduce && (
            <motion.span
              aria-hidden
              className="size-1 rounded-full bg-amber-300"
              animate={{ opacity: [1, 0.25, 1] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
            />
          )}
          轮到你
        </span>
        <span className="flex shrink-0 items-center gap-0.5 text-[10px] font-medium text-stone-500 transition-colors group-hover:text-stone-300">
          {open ? "收起" : "展开"}
          <motion.span
            aria-hidden
            className="flex"
            initial={false}
            animate={{ rotate: open ? 180 : 0 }}
            transition={reduce ? { duration: 0 } : { duration: 0.3, ease: EASE_OUT }}
          >
            <ChevronDown className="size-3.5" />
          </motion.span>
        </span>
      </button>

      {/* 展开态:完整 chip 序列(渲染逻辑原样)。始终挂载(inert 收起)→ 高度动画无布局跳动;
          open key 重放 stagger 入场;reduced-motion:高度瞬切、仅留透明度过渡 */}
      <motion.div
        id={regionId}
        inert={!open}
        initial={false}
        animate={open ? "open" : "collapsed"}
        variants={region}
        transition={
          reduce
            ? { height: { duration: 0 }, opacity: { duration: 0.15 } }
            : {
                height: { duration: 0.38, ease: EASE_OUT },
                opacity: { duration: 0.22, ease: "easeOut" },
              }
        }
        style={{ overflow: "hidden" }}
      >
        <motion.ol
          key={`${actionLine ?? ""}::${open ? "open" : "closed"}`}
          className="flex flex-wrap items-center gap-x-1 gap-y-2 pt-2.5"
          aria-label="行动线"
          variants={list}
          initial="hidden"
          animate="visible"
        >
          {steps.map((s, i) => {
            const isDecision = i === decisionIdx;
            const isFuture = i > decisionIdx;
            const showYou = s.isHero || (isDecision && !s.actor);
            return (
              <motion.li key={`${i}-${s.action}`} className="flex items-center gap-1" variants={item}>
                {i > 0 &&
                  (isFuture ? (
                    <span aria-hidden className="flex items-center pr-0.5 text-[10px] text-[#3E5245]/80">
                      <span className="inline-block w-2.5 border-t border-dashed border-current" />
                      →
                    </span>
                  ) : (
                    <span aria-hidden className="pr-0.5 text-[10px] text-[#3E5245]">
                      →
                    </span>
                  ))}
                <span
                  className={cn(
                    "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] leading-none",
                    isDecision
                      ? "border-amber-300/60 bg-[#261E0D] text-amber-100 shadow-[0_0_26px_-6px_rgba(228,184,91,0.55)]"
                      : isFuture
                        ? "border-dashed border-[#31453A] bg-transparent text-stone-500"
                        : s.isHero
                          ? "border-emerald-300/25 bg-emerald-400/10 text-stone-100"
                          : "border-[#243528] bg-[#101B14] text-stone-400",
                  )}
                >
                  {(s.actor || showYou) && (
                    <span
                      className={cn(
                        "rounded-full px-1.5 py-0.5 text-[9px] font-bold",
                        showYou
                          ? "bg-emerald-400/20 text-emerald-200"
                          : "bg-[#16211A] text-stone-400",
                      )}
                    >
                      {showYou ? "你" : renderGlossEntries(positionZh(s.actor), DECISION_SCREEN_GLOSS)}
                    </span>
                  )}
                  {isDecision && (
                    <span className="flex items-center gap-1 rounded-full bg-amber-300/20 px-1.5 py-0.5 text-[9px] font-bold text-amber-200">
                      {!reduce && (
                        <motion.span
                          aria-hidden
                          className="size-1 rounded-full bg-amber-300"
                          animate={{ opacity: [1, 0.25, 1] }}
                          transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
                        />
                      )}
                      决策时刻
                    </span>
                  )}
                  {isFuture && i === decisionIdx + 1 && (
                    <span
                      title="这是「如果你这么选」之后可能发生的事,还没发生"
                      className="rounded-full bg-[#16211A] px-1.5 py-0.5 text-[9px] font-bold text-stone-500 ring-1 ring-white/5"
                    >
                      假设
                    </span>
                  )}
                  {/* D' 行动线挂白话点词 + C' 同花XX/单花面消毒:行动来路一眼读懂 */}
                  <span className={cn(isDecision && "font-semibold")}>
                    {renderGlossEntries(sanitizeSuitedZh(jargonZh(s.action)), DECISION_SCREEN_GLOSS)}
                  </span>
                </span>
              </motion.li>
            );
          })}
        </motion.ol>
      </motion.div>
    </div>
  );
}
